// Consolidated Mathematics Topics Data - M01 to M23
// Systematic organization for Supabase migration

import { mathTopics as mathTopicsM01M05 } from './mathTopics';
import { mathTopicsM06M23 } from './mathTopicsM06M23';
import { mathTopicsM09M15 } from './mathTopicsM09M15';
import { mathTopicsM16M23 } from './mathTopicsM16M23';

// Merge all topics into a single comprehensive data structure
export const allMathTopics = {
  // M01-M05 (Already implemented)
  ...mathTopicsM01M05,
  
  // M06-M08 (From mathTopicsM06M23)
  M06: mathTopicsM06M23.M06,
  M07: mathTopicsM06M23.M07,
  M08: mathTopicsM06M23.M08,
  
  // M09-M15 (From mathTopicsM09M15)
  M09: mathTopicsM09M15.M09,
  M10: mathTopicsM09M15.M10,
  
  // M11-M15 (Need to be completed)
  M11: {
    id: 'M11',
    title: '率、比與變分',
    titleEn: 'Rates, ratios and variations',
    description: '學習率、比例和變分的概念及應用',
    icon: '⚖️',
    modules: {
      M11_01: {
        id: 'M11_01',
        title: '比例和比率',
        titleEn: 'Ratios and Proportions',
        description: '比例關係的基本概念',
        examples: [
          {
            id: 'M11_01_01',
            question: '如果 a:b = 3:4，b:c = 2:5，求 a:b:c',
            answer: '3:4:10',
            solution: '統一b的比值：a:b = 3:4，b:c = 4:10，所以 a:b:c = 3:4:10',
            difficulty: 'basic'
          },
          {
            id: 'M11_01_02',
            question: '兩個數的比為3:5，它們的和為64，求這兩個數',
            answer: '24 和 40',
            solution: '設兩數為3x和5x，則3x + 5x = 64，x = 8，所以兩數為24和40',
            difficulty: 'basic'
          },
          {
            id: 'M11_01_03',
            question: '三個數成比例，第一個數是12，第二個數是18，第三個數是多少？',
            answer: '27',
            solution: '設第三個數為x，則12:18 = 18:x，12x = 18²，x = 324/12 = 27',
            difficulty: 'intermediate'
          },
          {
            id: 'M11_01_04',
            question: '某合金中銅、鋅、錫的比例為5:3:2，要製造100kg這種合金，需要各金屬多少kg？',
            answer: '銅50kg，鋅30kg，錫20kg',
            solution: '總比例 = 5+3+2 = 10，銅：100×5/10 = 50kg，鋅：100×3/10 = 30kg，錫：100×2/10 = 20kg',
            difficulty: 'intermediate'
          },
          {
            id: 'M11_01_05',
            question: '黃金比例的值是多少？',
            answer: '(1+√5)/2 ≈ 1.618',
            solution: '黃金比例 φ = (1+√5)/2，這是方程 x² - x - 1 = 0 的正根',
            difficulty: 'advanced'
          }
        ]
      }
    }
  },
  
  M12: {
    id: 'M12',
    title: '等差數列與等比數列',
    titleEn: 'Arithmetic and geometric sequences',
    description: '學習等差數列和等比數列的性質及應用',
    icon: '📈',
    modules: {
      M12_01: {
        id: 'M12_01',
        title: '等差數列',
        titleEn: 'Arithmetic Sequences',
        description: '等差數列的通項公式和求和公式',
        examples: [
          {
            id: 'M12_01_01',
            question: '等差數列 2, 5, 8, 11, ... 的第10項是多少？',
            answer: '29',
            solution: '首項a₁ = 2，公差d = 3，第n項 aₙ = a₁ + (n-1)d = 2 + 9×3 = 29',
            difficulty: 'basic'
          },
          {
            id: 'M12_01_02',
            question: '等差數列前n項和公式是什麼？',
            answer: 'Sₙ = n(a₁ + aₙ)/2 或 Sₙ = na₁ + n(n-1)d/2',
            solution: '等差數列前n項和有兩個公式：Sₙ = n(a₁ + aₙ)/2 或 Sₙ = na₁ + n(n-1)d/2',
            difficulty: 'basic'
          },
          {
            id: 'M12_01_03',
            question: '等差數列中，a₃ = 7，a₇ = 19，求a₁和d',
            answer: 'a₁ = 1, d = 3',
            solution: 'a₃ = a₁ + 2d = 7，a₇ = a₁ + 6d = 19，解得d = 3，a₁ = 1',
            difficulty: 'intermediate'
          },
          {
            id: 'M12_01_04',
            question: '等差數列1, 4, 7, 10, ... 的前20項和是多少？',
            answer: '590',
            solution: 'a₁ = 1, d = 3, n = 20，S₂₀ = 20×1 + 20×19×3/2 = 20 + 570 = 590',
            difficulty: 'intermediate'
          },
          {
            id: 'M12_01_05',
            question: '等差數列中，若a₂ + a₄ + a₆ = 12，求a₁ + a₃ + a₅的值',
            answer: '12',
            solution: '設等差數列公差為d，則a₂ + a₄ + a₆ = 3a₄ = 12，所以a₄ = 4，同理a₁ + a₃ + a₅ = 3a₃ = 3(a₄ - d) = 12',
            difficulty: 'advanced'
          }
        ]
      }
    }
  },
  
  M13: {
    id: 'M13',
    title: '圓的性質',
    titleEn: 'Properties of circles',
    description: '學習圓的幾何性質和定理',
    icon: '⭕',
    modules: {
      M13_01: {
        id: 'M13_01',
        title: '圓的基本性質',
        titleEn: 'Basic Properties of Circles',
        description: '圓的半徑、直徑、弦等基本概念',
        examples: [
          {
            id: 'M13_01_01',
            question: '圓的半徑為5，求圓的周長和面積',
            answer: '周長 = 10π，面積 = 25π',
            solution: '周長 C = 2πr = 10π，面積 S = πr² = 25π',
            difficulty: 'basic'
          },
          {
            id: 'M13_01_02',
            question: '圓中最長的弦是什麼？',
            answer: '直徑',
            solution: '直徑是通過圓心的弦，是圓中最長的弦',
            difficulty: 'basic'
          },
          {
            id: 'M13_01_03',
            question: '圓的半徑為13，弦長為24，求弦心距',
            answer: '5',
            solution: '弦心距 = √(r² - (弦長/2)²) = √(13² - 12²) = √(169 - 144) = 5',
            difficulty: 'intermediate'
          },
          {
            id: 'M13_01_04',
            question: '同心圓的半徑分別為3和5，求環形面積',
            answer: '16π',
            solution: '環形面積 = π×5² - π×3² = 25π - 9π = 16π',
            difficulty: 'basic'
          },
          {
            id: 'M13_01_05',
            question: '圓內接正六邊形的邊長與圓半徑的關係是什麼？',
            answer: '邊長 = 半徑',
            solution: '正六邊形的邊長等於圓的半徑',
            difficulty: 'intermediate'
          }
        ]
      }
    }
  },
  
  M14: {
    id: 'M14',
    title: '求積法',
    titleEn: 'Mensuration',
    description: '學習各種幾何圖形的面積和體積計算',
    icon: '📐',
    modules: {
      M14_01: {
        id: 'M14_01',
        title: '平面圖形面積',
        titleEn: 'Area of Plane Figures',
        description: '各種平面圖形的面積計算',
        examples: [
          {
            id: 'M14_01_01',
            question: '三角形底邊8，高6，求面積',
            answer: '24',
            solution: '三角形面積 = (1/2) × 底 × 高 = (1/2) × 8 × 6 = 24',
            difficulty: 'basic'
          },
          {
            id: 'M14_01_02',
            question: '梯形上底4，下底10，高5，求面積',
            answer: '35',
            solution: '梯形面積 = (1/2) × (上底 + 下底) × 高 = (1/2) × (4 + 10) × 5 = 35',
            difficulty: 'basic'
          },
          {
            id: 'M14_01_03',
            question: '圓的半徑為4，求扇形（圓心角90°）的面積',
            answer: '4π',
            solution: '扇形面積 = (θ/360°) × πr² = (90°/360°) × π × 4² = 4π',
            difficulty: 'intermediate'
          },
          {
            id: 'M14_01_04',
            question: '正六邊形邊長為2，求面積',
            answer: '6√3',
            solution: '正六邊形面積 = (3√3/2) × a² = (3√3/2) × 4 = 6√3',
            difficulty: 'intermediate'
          },
          {
            id: 'M14_01_05',
            question: '橢圓長軸10，短軸6，求面積',
            answer: '15π',
            solution: '橢圓面積 = πab，其中a = 5, b = 3，面積 = π × 5 × 3 = 15π',
            difficulty: 'intermediate'
          }
        ]
      }
    }
  },
  
  M15: {
    id: 'M15',
    title: '三角學',
    titleEn: 'Trigonometry',
    description: '學習三角函數的基本概念和應用',
    icon: '📐',
    modules: {
      M15_01: {
        id: 'M15_01',
        title: '三角函數基礎',
        titleEn: 'Basic Trigonometric Functions',
        description: '正弦、餘弦、正切函數的定義',
        examples: [
          {
            id: 'M15_01_01',
            question: '在直角三角形中，sin 30° = ?',
            answer: '1/2',
            solution: 'sin 30° = 對邊/斜邊 = 1/2',
            difficulty: 'basic'
          },
          {
            id: 'M15_01_02',
            question: '計算 cos 60°',
            answer: '1/2',
            solution: 'cos 60° = 鄰邊/斜邊 = 1/2',
            difficulty: 'basic'
          },
          {
            id: 'M15_01_03',
            question: '計算 tan 45°',
            answer: '1',
            solution: 'tan 45° = 對邊/鄰邊 = 1',
            difficulty: 'basic'
          },
          {
            id: 'M15_01_04',
            question: '基本三角恆等式 sin²θ + cos²θ = ?',
            answer: '1',
            solution: '這是畢達哥拉斯恆等式：sin²θ + cos²θ = 1',
            difficulty: 'basic'
          },
          {
            id: 'M15_01_05',
            question: '計算 sin 90°',
            answer: '1',
            solution: 'sin 90° = 1，這是三角函數的特殊值',
            difficulty: 'basic'
          }
        ]
      }
    }
  },
  
  // M16-M23 (From mathTopicsM16M23)
  M16: mathTopicsM16M23.M16,
  M17: mathTopicsM16M23.M17,
  M18: mathTopicsM16M23.M18,
  M19: mathTopicsM16M23.M19,
  M20: mathTopicsM16M23.M20,
  M21: mathTopicsM16M23.M21,
  M22: mathTopicsM16M23.M22,
  M23: mathTopicsM16M23.M23
};

// Export topic categories for navigation
export const mathTopicCategories = [
  {
    id: 'number_algebra',
    title: '數與代數',
    titleEn: 'Number and Algebra',
    topics: ['M01', 'M02', 'M03', 'M07', 'M08', 'M09', 'M10', 'M11', 'M12']
  },
  {
    id: 'geometry',
    title: '幾何',
    titleEn: 'Geometry',
    topics: ['M04', 'M06', 'M13', 'M14', 'M15', 'M16', 'M17', 'M18', 'M19']
  },
  {
    id: 'statistics_probability',
    title: '統計與概率',
    titleEn: 'Statistics and Probability',
    topics: ['M20', 'M21', 'M22']
  },
  {
    id: 'applications',
    title: '應用與綜合',
    titleEn: 'Applications and Integration',
    topics: ['M05', 'M23']
  }
];

// Export helper functions
export const getTopicById = (topicId) => {
  return allMathTopics[topicId];
};

export const getModuleById = (topicId, moduleId) => {
  const topic = allMathTopics[topicId];
  return topic?.modules?.[moduleId];
};

export const getExampleById = (topicId, moduleId, exampleId) => {
  const module = getModuleById(topicId, moduleId);
  return module?.examples?.find(example => example.id === exampleId);
};

export const getAllTopicsList = () => {
  return Object.values(allMathTopics).map(topic => ({
    id: topic.id,
    title: topic.title,
    titleEn: topic.titleEn,
    description: topic.description,
    icon: topic.icon,
    moduleCount: Object.keys(topic.modules || {}).length
  }));
};

export const searchTopics = (query) => {
  const lowercaseQuery = query.toLowerCase();
  return Object.values(allMathTopics).filter(topic => 
    topic.title.toLowerCase().includes(lowercaseQuery) ||
    topic.titleEn.toLowerCase().includes(lowercaseQuery) ||
    topic.description.toLowerCase().includes(lowercaseQuery)
  );
};

export default allMathTopics; 